// File: src/pages/RoleList.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Card, Button, Input, Spinner } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { FaPlus, FaPencilAlt, FaEye } from "react-icons/fa";
import { CheckPermission } from "../import/CheckPermission";
import swal from "sweetalert";

// ✅ API imports via src/import as per your rules
import { Get_RoleList } from "../import/ApiEndPoint/ApiCalling";
import { Role_list_by_Master } from "../import/ApiEndPoint/Api";

const injectOnce = () => {
  if (document.getElementById("role-inline-css")) return;
  const s = document.createElement("style");
  s.id = "role-inline-css";
  s.textContent = `
    .p-toolbar .count-pill{ background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px; }
    .p-card{ border-radius:14px; border:1px solid #e5e7eb; }
    .p-table-wrap{ width:100%; overflow:auto; border:1px solid #e5e7eb; border-radius:14px; background:#fff; }
    .p-table{ width:100%; border-collapse:collapse; table-layout:auto; min-width:900px; }
    .p-table thead th{ background:#f4f7f6; color:#0f172a; font-weight:700; text-transform:uppercase; font-size:12px; letter-spacing:.03em; border-bottom:1px solid #e5e7eb; padding:6px 8px; text-align:center; white-space:nowrap; }
    .p-table tbody td{ padding:6px 8px; border-bottom:1px solid #f1f5f9; vertical-align:middle; color:#0f172a; font-size:13px; text-align:center; }
    .p-table tbody tr:hover{ background:#f9fbff; }
    .actions{ display:flex; justify-content:center; align-items:center; gap:8px; }
    .chip{ display:inline-grid; place-items:center; width:28px; height:28px; border-radius:16px; cursor:pointer; }
    .chip-edit{ background:#d46f10; }
  `;
  document.head.appendChild(s);
};

const toStr = (v) => (v === null || v === undefined ? "" : String(v));

export default function RoleList() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]); // API: res?.Role
  const [search, setSearch] = useState("");
  const [permissions, setPermissions] = useState({});
  const [user, setUser] = useState({});

  useEffect(() => injectOnce(), []);

  useEffect(() => {
    try {
      const u = JSON.parse(localStorage.getItem("userData") || "{}");
      setUser(u);
      setPermissions(CheckPermission("Create User") || {});
      (async () => {
        setLoading(true);
        try {
          // MASTER fetch path vs normal
          const isMaster = u?.rolename?.roleName === "MASTER";
          let res;
          if (isMaster) {
            // In original code: Role_list_by_Master(superadminId)
            res = await fetch(Role_list_by_Master + "/" + (u?._id || ""));
            const data = await res.json();
            setRows(Array.isArray(data?.Role) ? data.Role : []);
          } else {
            // In original code: Get_RoleList(userId, db)
            const out = await Get_RoleList(u?._id, u?.database);
            setRows(Array.isArray(out?.Role) ? out.Role : []);
          }
        } catch (err) {
          console.error("[RoleList] load error:", err);
          setRows([]);
          swal("Failed to load roles");
        } finally {
          setLoading(false);
        }
      })();
    } catch {
      setLoading(false);
    }
  }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    const hit = (v) => toStr(v).toLowerCase().includes(term);
    return rows.filter(
      (r) => hit(r?.roleName) || hit(r?.id || r?._id) || hit(r?.desc)
    );
  }, [rows, search]);

  const canEdit = (row) => {
    const createdById = row?.createdBy?._id || row?.createdBy;
    const isOwner = createdById && user?._id && createdById === user._id;
    const isMaster = user?.rolename?.roleName === "MASTER";
    return permissions?.Edit !== false && (isOwner || isMaster);
  };

  const exportCSV = () => {
    if (!filtered.length) return swal("No data to export");
    const head = ["S.NO", "ROLE NAME", "ROLE ID", "ROLE DESC"];
    const body = filtered.map((r, i) => [
      i + 1,
      toStr(r?.roleName),
      toStr(r?.id || r?._id),
      toStr(r?.desc),
    ]);
    const csv = [head, ...body]
      .map((row) =>
        row.map((x) => `"${String(x).replace(/"/g, '""')}"`).join(",")
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "RoleList.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  return (
    <>
      {/* ===== Toolbar (ProductListCompact style) ===== */}
      <Row className="p-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card
            className="p-card"
            style={{ padding: 8, background: "#f4f7f6" }}
          >
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 10,
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  fontSize: 18,
                }}
              >
                Role List <span className="count-pill">{filtered.length}</span>
              </div>

              <div style={{ flex: "1 1 240px" }} />

              <div
                style={{
                  minWidth: 260,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Input
                  placeholder="Search roles..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              {permissions?.Download && (
                <Button
                  outline
                  color="primary"
                  style={{ height: 32 }}
                  onClick={exportCSV}
                >
                  Export .CSV
                </Button>
              )}

              {permissions?.Create && (
                <>
                  <Button
                    style={{
                      backgroundColor: "rgb(8, 91, 245)",
                      color: "white",
                      fontWeight: 600,
                      height: 32,
                    }}
                    onClick={() => navigate("/app/departments")}
                  >
                    Department
                  </Button>
                  <Button
                    style={{
                      backgroundColor: "rgb(8, 91, 245)",
                      color: "white",
                      fontWeight: 600,
                      height: 32,
                    }}
                    onClick={() => navigate("/app/view-heirarchy")}
                  >
                    Hierarchy
                  </Button>
                  <Button
                    style={{
                      backgroundColor: "rgb(8, 91, 245)",
                      color: "white",
                      fontWeight: 600,
                      height: 32,
                    }}
                    onClick={() => navigate("/app/roles/create")}
                  >
                    <FaPlus size={12} /> Role
                  </Button>
                </>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      {/* ===== Table (no pagination) ===== */}
      <Row>
        <Col sm="12">
          <Card className="p-card" style={{ padding: 8 }}>
            <div className="p-table-wrap">
              <table className="p-table">
                <thead>
                  <tr>
                    <th>S.NO</th>
                    <th>ROLE NAME</th>
                    <th>ROLE ID</th>
                    <th>ROLE DESC</th>
                    <th>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={5} style={{ padding: 18, color: "#6b7280" }}>
                        No roles found.
                      </td>
                    </tr>
                  ) : (
                    filtered.map((r, i) => {
                      const roleId = r?.id || r?._id;
                      const roleMainId = r?._id;
                      return (
                        <tr key={roleId || i}>
                          <td>{i + 1}</td>
                          <td style={{ textAlign: "left" }}>
                            {r?.roleName || "-"}
                          </td>
                          <td>{roleId || "-"}</td>
                          <td style={{ textAlign: "left" }}>
                            {r?.desc || "-"}
                          </td>
                          <td>
                            <div className="actions">
                              {canEdit(r) && (
                                <span
                                  className="chip chip-edit"
                                  title="Edit"
                                  onClick={() =>
                                    navigate(
                                      `/app/freshlist/account/editRole/${roleId}`
                                    )
                                  }
                                >
                                  <FaPencilAlt size={14} color="white" />
                                </span>
                              )}
                              <span
                                className="chip"
                                style={{ background: "#374151" }}
                                title="View Permissions"
                                onClick={() =>
                                  navigate(`/app/roles/view/${roleMainId}`)
                                }
                              >
                                <FaEye size={14} color="white" />
                              </span>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </Col>
      </Row>
    </>
  );
}
